import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Fade,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDrag, useDrop } from "react-dnd";
import { Howl } from "howler";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GameHUD from "./components/GameHUD";
import HowToPlayModal from "./components/HowToPlayModal";
import {
  getPonderStreakFromStorage,
  getIsPonderStreakActive,
} from "../components/PonderStreakUtil";

// Sound instances
const clickSound = new Howl({
  src: ["/assets/audio/click.ogg", "/assets/audio/click.mp3"],
  volume: 0.5,
});

const buttonClickSound = new Howl({
  src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
  volume: 0.2,
});

// Game data
const GAME_DATA = {
  categories: ["Kitchen Tools", "Made of Metal", "Has Handle"],
  words: [
    { word: "Blender", belongsIn: ["Kitchen Tools"] },
    { word: "Coins", belongsIn: ["Made of Metal"] },
    { word: "Umbrella", belongsIn: ["Has Handle"] },
    { word: "Colander", belongsIn: ["Kitchen Tools", "Made of Metal"] },
    { word: "Mug", belongsIn: ["Kitchen Tools", "Has Handle"] },
    { word: "Hammer", belongsIn: ["Made of Metal", "Has Handle"] },
    {
      word: "Ladle",
      belongsIn: ["Kitchen Tools", "Made of Metal", "Has Handle"],
    },
  ],
};

const THEMES = GAME_DATA.categories;
const WORDS = GAME_DATA.words.map((item) => item.word);

// Generate correct word placements based on the new data structure
const CORRECT_PLACEMENTS = {
  "A-only": [],
  "B-only": [],
  "C-only": [],
  "A-B": [],
  "B-C": [],
  "A-C": [],
  "A-B-C": [],
};

// Populate correct placements based on the new data structure
GAME_DATA.words.forEach((item) => {
  const categories = item.belongsIn;

  if (categories.length === 1) {
    // Single category items
    if (categories.includes("Kitchen Tools")) {
      CORRECT_PLACEMENTS["A-only"].push(item.word);
    } else if (categories.includes("Made of Metal")) {
      CORRECT_PLACEMENTS["B-only"].push(item.word);
    } else if (categories.includes("Has Handle")) {
      CORRECT_PLACEMENTS["C-only"].push(item.word);
    }
  } else if (categories.length === 2) {
    // Two category items
    if (
      categories.includes("Kitchen Tools") &&
      categories.includes("Made of Metal")
    ) {
      CORRECT_PLACEMENTS["A-B"].push(item.word);
    } else if (
      categories.includes("Made of Metal") &&
      categories.includes("Has Handle")
    ) {
      CORRECT_PLACEMENTS["B-C"].push(item.word);
    } else if (
      categories.includes("Kitchen Tools") &&
      categories.includes("Has Handle")
    ) {
      CORRECT_PLACEMENTS["A-C"].push(item.word);
    }
  } else if (categories.length === 3) {
    // Three category items
    CORRECT_PLACEMENTS["A-B-C"].push(item.word);
  }
});

// Updated theme colors with pastel fills
const THEME_COLORS = {
  Kitchen: "rgba(255, 107, 107, 0.25)", // Soft red
  Heavy: "rgba(78, 205, 196, 0.25)", // Soft teal
  Hair: "rgba(69, 183, 209, 0.25)", // Soft blue
};

// Drop zone configurations for the 7 regions
const DROP_ZONES = {
  "A-only": {
    position: { top: "10%", left: "6%", width: "28%", height: "28%" },
    backgroundColor: "rgba(255, 107, 107, 0.1)",
    borderColor: "rgba(255, 107, 107, 0.3)",
  },
  "B-only": {
    position: { top: "10%", left: "66%", width: "28%", height: "28%" },
    backgroundColor: "rgba(78, 205, 196, 0.1)",
    borderColor: "rgba(78, 205, 196, 0.3)",
  },
  "C-only": {
    position: { top: "66%", left: "36%", width: "28%", height: "28%" },
    backgroundColor: "rgba(69, 183, 209, 0.1)",
    borderColor: "rgba(69, 183, 209, 0.3)",
  },
  "A-B": {
    position: { top: "10%", left: "36%", width: "28%", height: "28%" },
    backgroundColor: "rgba(255, 107, 107, 0.05)",
    borderColor: "rgba(255, 107, 107, 0.2)",
  },
  "B-C": {
    position: { top: "38%", left: "66%", width: "28%", height: "28%" },
    backgroundColor: "rgba(78, 205, 196, 0.05)",
    borderColor: "rgba(78, 205, 196, 0.2)",
  },
  "A-C": {
    position: { top: "38%", left: "6%", width: "28%", height: "28%" },
    backgroundColor: "rgba(69, 183, 209, 0.05)",
    borderColor: "rgba(69, 183, 209, 0.2)",
  },
  "A-B-C": {
    position: { top: "38%", left: "36%", width: "28%", height: "28%" },
    backgroundColor: "rgba(255, 107, 107, 0.03)",
    borderColor: "rgba(255, 107, 107, 0.15)",
  },
};

// Draggable Word Component
const DraggableWord = ({
  word,
  isInBank,
  isLocked,
  isIncorrect,
  onDrop,
  guessesLeft,
  showPulse,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: "word",
    item: { word },
    canDrag: !isLocked,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Add visual feedback for mobile dragging
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (!isLocked) {
        setIsTouchDragging(true);
        const touch = e.touches[0];
        setTouchPosition({ x: touch.clientX, y: touch.clientY });
      }
    };

    const handleTouchMove = (e) => {
      if (isTouchDragging) {
        const touch = e.touches[0];
        setTouchPosition({ x: touch.clientX, y: touch.clientY });
      }
    };

    const handleTouchEnd = () => {
      setIsTouchDragging(false);
    };

    const element = document.querySelector(`[data-word="${word}"]`);
    if (element) {
      element.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      element.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      element.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        element.removeEventListener("touchstart", handleTouchStart);
        element.removeEventListener("touchmove", handleTouchMove);
        element.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [word, isLocked, isTouchDragging]);

  // Pulse animation for Word Bank words only if showPulse is true
  const pulse = isInBank && showPulse && !isLocked && !isIncorrect;

  return (
    <>
      {/* Mobile Drag Indicator */}
      {isTouchDragging && (
        <Box
          sx={{
            position: "fixed",
            left: touchPosition.x - 50,
            top: touchPosition.y - 25,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <Chip
            label={word}
            sx={{
              bgcolor: "#fff",
              border: "2px solid #8FBDC4",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              transform: "scale(1.2)",
              "& .MuiChip-label": {
                padding: "8px 18px",
                fontSize: "clamp(14px, 3vw, 16px)",
                fontWeight: 600,
                color: "#222",
              },
            }}
          />
        </Box>
      )}

      <Box
        ref={drag}
        data-word={word}
        sx={{
          opacity: isDragging ? 0.5 : isLocked ? 0.85 : 1,
          cursor: isLocked ? "default" : "grab",
          "&:active": { cursor: isLocked ? "default" : "grabbing" },
          minHeight: "44px",
          minWidth: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: isLocked ? "scale(1.05)" : "scale(1)",
          transition: "transform 0.3s ease, opacity 0.3s ease",
          touchAction: "none",
        }}
      >
        <Chip
          label={word}
          className={isIncorrect ? "shake" : pulse ? "pulse" : ""}
          sx={{
            m: { xs: 0.15, sm: 0.5 },
            bgcolor: isInBank
              ? isIncorrect
                ? "#fce3e3"
                : "#fff"
              : isLocked
              ? "#e9f8e6"
              : "rgba(0,0,0,0.05)",
            border: isLocked
              ? "2px solid #66b66b"
              : isIncorrect
              ? "1px solid #e48f8f"
              : isInBank
              ? "1px solid #aaa"
              : "none",
            boxShadow:
              isInBank && !isLocked && !isIncorrect
                ? "0 1px 4px rgba(0,0,0,0.04)"
                : "none",
            "&:hover": {
              bgcolor: isLocked
                ? "#e9f8e6"
                : isInBank
                ? "#f5f5f5"
                : "rgba(0,0,0,0.15)",
              borderColor:
                isInBank && !isLocked && !isIncorrect ? "#888" : undefined,
              boxShadow:
                isInBank && !isLocked && !isIncorrect
                  ? "0 2px 8px rgba(0,0,0,0.10)"
                  : undefined,
              transform: isLocked ? "scale(1.05)" : "translateY(-1px)",
            },
            transition: "all 0.2s ease",
            minHeight: { xs: 32, sm: 44 },
            "& .MuiChip-label": {
              padding: { xs: "4px 10px", sm: "8px 18px" },
              fontSize: { xs: "0.75rem", sm: "clamp(14px, 3vw, 16px)" },
              fontWeight: 600,
              color: isLocked ? "#226622" : isIncorrect ? "#5c1d1d" : "#222",
              wordBreak: "break-word",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "100%",
              lineHeight: 1.2,
              display: "block",
            },
            "@keyframes shake": {
              "0%": { transform: "translateX(0)" },
              "20%": { transform: "translateX(-4px)" },
              "40%": { transform: "translateX(4px)" },
              "60%": { transform: "translateX(-4px)" },
              "80%": { transform: "translateX(4px)" },
              "100%": { transform: "translateX(0)" },
            },
            "&.shake": {
              animation: "shake 0.4s ease",
            },
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 #8FBDC4" },
              "70%": { boxShadow: "0 0 0 8px rgba(143,189,196,0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(143,189,196,0)" },
            },
            "&.pulse": {
              animation: "pulse 1.2s infinite",
            },
          }}
        />
      </Box>
    </>
  );
};

// Drop Zone Component
const DropZone = ({
  zoneId,
  zoneConfig,
  placedWord,
  onDrop,
  onRemove,
  isLocked,
  showSolution,
  getZoneCategories,
  setsAreEqual,
}) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "word",
    drop: (item) => onDrop(zoneId, item.word),
    canDrop: () => !isLocked,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const handleRemove = () => {
    if (placedWord && !isLocked) {
      onRemove(zoneId);
    }
  };

  // Calculate font size based on word length
  const getFontSize = (word) => {
    if (!word) return { xs: "0.6rem", sm: "clamp(0.7rem, 1.5vw, 1rem)" };
    const length = word.length;
    if (length <= 6)
      return { xs: "0.7rem", sm: "clamp(0.8rem, 1.8vw, 1.1rem)" };
    if (length <= 10)
      return { xs: "0.65rem", sm: "clamp(0.75rem, 1.6vw, 1rem)" };
    return { xs: "0.6rem", sm: "clamp(0.7rem, 1.4vw, 0.9rem)" };
  };

  // Determine background opacity based on state
  const getBackgroundOpacity = () => {
    if (placedWord) return 0; // Fade to zero when word is placed
    if (isOver && canDrop) return 0.3; // Highlight on hover/drag
    return 0; // Invisible by default
  };

  // Get solution words for this zone using set comparison
  const getSolutionWordsForZone = (zoneId) => {
    const zoneCategories = getZoneCategories(zoneId);
    return GAME_DATA.words
      .filter((wordData) =>
        setsAreEqual(new Set(wordData.belongsIn), zoneCategories)
      )
      .map((wordData) => wordData.word);
  };

  const solutionWords = getSolutionWordsForZone(zoneId);

  return (
    <Box
      ref={drop}
      sx={{
        position: "absolute",
        ...zoneConfig.position,
        backgroundColor: `rgba(143, 189, 196, ${getBackgroundOpacity()})`,
        borderRadius: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "default",
        transition: "all 0.2s ease",
        minHeight: { xs: "50px", sm: "60px" },
        padding: "0",
        zIndex: 2,
        // Enhanced hover feedback with box-shadow
        boxShadow:
          isOver && canDrop
            ? "0 0 0 2px rgba(0, 0, 0, 0.15), 0 0 12px rgba(143, 189, 196, 0.4)"
            : "none",
        // Pulse animation for occupied zones when dragging over
        animation: isOver && placedWord ? "pulse 0.6s ease-in-out" : "none",
        "@keyframes pulse": {
          "0%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.05)" },
          "100%": { transform: "scale(1)" },
        },
        "&:hover": {
          backgroundColor:
            placedWord && !isLocked
              ? "rgba(143, 189, 196, 0.1)"
              : "rgba(143, 189, 196, 0.2)",
          boxShadow:
            placedWord && !isLocked
              ? "0 0 8px rgba(143, 189, 196, 0.3)"
              : "0 0 0 2px rgba(0, 0, 0, 0.15), 0 0 12px rgba(143, 189, 196, 0.4)",
        },
      }}
    >
      {placedWord && (
        <DraggableWord
          word={placedWord}
          isInBank={false}
          isLocked={isLocked}
          isIncorrect={false}
          onDrop={onDrop}
          guessesLeft={5}
          showPulse={false}
        />
      )}

      {/* Show solution words when game is over */}
      {showSolution && solutionWords.length > 0 && !placedWord && (
        <Box
          sx={{
            backgroundColor: "rgba(255, 193, 7, 0.8)",
            borderRadius: "1rem",
            padding: "2px 6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "90%",
            transform: "translateY(-2px)",
            border: "2px solid rgba(255, 193, 7, 0.8)",
          }}
        >
          <Typography
            sx={{
              fontSize: getFontSize(solutionWords[0]),
              fontWeight: 700,
              textAlign: "center",
              color: "#e65100",
              wordBreak: "break-word",
              lineHeight: 1.1,
              paddingInline: { xs: "2px", sm: "4px" },
              userSelect: "none",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
            }}
          >
            {solutionWords.join(", ")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Word Bank Component
const WordBank = ({
  words,
  onDrop,
  incorrectWords,
  guessesLeft,
  showPulse,
}) => {
  const [{ isOver }, drop] = useDrop({
    accept: "word",
    drop: (item) => onDrop(null, item.word),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <Box
      ref={drop}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: { xs: 0.5, sm: 1 },
        rowGap: { xs: 0.25, sm: 0.5 },
        p: 0,
        border: `2px dashed ${isOver ? "#8FBDC4" : "rgba(0,0,0,0.1)"}`,
        borderRadius: 2,
        bgcolor: isOver ? "rgba(143, 189, 196, 0.1)" : "rgba(0,0,0,0.02)",
        transition: "all 0.2s ease",
        minHeight: { xs: 36, sm: 44 },
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      {words.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: "center",
            width: "100%",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          Drag words here to remove them
        </Typography>
      ) : (
        words.map((word) => (
          <DraggableWord
            key={word}
            word={word}
            isInBank={true}
            isIncorrect={incorrectWords.includes(word)}
            guessesLeft={guessesLeft}
            showPulse={showPulse}
          />
        ))
      )}
    </Box>
  );
};

// Main Component
const CommonThreeBoard = () => {
  const theme = useTheme();
  const [currentDate] = useState(new Date());
  const [streak, setStreak] = useState(0);
  const [isStreakActive, setIsStreakActive] = useState(false);
  const [showHowToModal, setShowHowToModal] = useState(false);
  const [placedWords, setPlacedWords] = useState({});
  const [availableWords, setAvailableWords] = useState([...WORDS]);
  const [guessesLeft, setGuessesLeft] = useState(5);
  const [lockedWords, setLockedWords] = useState({}); // Track correctly placed words
  const [incorrectWords, setIncorrectWords] = useState([]); // Track words that were incorrect
  const [gameState, setGameState] = useState("playing"); // "playing", "won", "lost"
  const [showResult, setShowResult] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  // Helper function to get categories represented by a zone
  const getZoneCategories = (zoneId) => {
    const zoneCategoryMap = {
      "A-only": new Set(["Kitchen Tools"]),
      "B-only": new Set(["Made of Metal"]),
      "C-only": new Set(["Has Handle"]),
      "A-B": new Set(["Kitchen Tools", "Made of Metal"]),
      "B-C": new Set(["Made of Metal", "Has Handle"]),
      "A-C": new Set(["Kitchen Tools", "Has Handle"]),
      "A-B-C": new Set(["Kitchen Tools", "Made of Metal", "Has Handle"]),
    };

    return zoneCategoryMap[zoneId] || new Set();
  };

  // Helper function to compare two sets
  const setsAreEqual = (set1, set2) => {
    if (set1.size !== set2.size) return false;
    for (const item of set1) {
      if (!set2.has(item)) return false;
    }
    return true;
  };

  // Pulse state for word bank
  const [showPulse, setShowPulse] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const { count } = getPonderStreakFromStorage("commonthree");
    setStreak(count);
    setIsStreakActive(getIsPonderStreakActive("commonthree"));
  }, [currentDate]);

  const toggleHowToModal = () => {
    buttonClickSound.play();
    setShowHowToModal((prev) => !prev);
  };

  const handleDateClick = () => {
    // For now, just play a sound - could be expanded to show date info
    buttonClickSound.play();
  };

  const handleDrop = (zoneId, word) => {
    clickSound.play();

    // Find the zone where this word is currently placed (if any)
    const existingZone = Object.keys(placedWords).find(
      (key) => placedWords[key] === word
    );
    if (existingZone) {
      setPlacedWords((prev) => {
        const newPlaced = { ...prev };
        delete newPlaced[existingZone];
        return newPlaced;
      });
    } else {
      // Remove word from available words only if it's not already placed
      setAvailableWords((prev) => prev.filter((w) => w !== word));
    }

    // If the target zone is already occupied, return the existing word to the bank
    if (placedWords[zoneId]) {
      setAvailableWords((prev) => [...prev, placedWords[zoneId]]);
    }

    // Place the word in the new zone
    setPlacedWords((prev) => ({
      ...prev,
      [zoneId]: word,
    }));
  };

  const handleRemoveWord = (zoneId) => {
    clickSound.play();
    const word = placedWords[zoneId];

    // Only allow removal if word is not locked
    if (lockedWords[zoneId]) return;

    // Remove word from placed words
    setPlacedWords((prev) => {
      const newPlaced = { ...prev };
      delete newPlaced[zoneId];
      return newPlaced;
    });

    // Add word back to available words
    setAvailableWords((prev) => [...prev, word]);
  };

  const checkPlacements = () => {
    const correct = [];
    const incorrect = [];

    // First pass: check each placement individually
    Object.entries(placedWords).forEach(([zoneId, word]) => {
      // Find the word data to get its correct categories
      const wordData = GAME_DATA.words.find((w) => w.word === word);
      if (!wordData) {
        incorrect.push(zoneId);
        return;
      }

      // Get the correct categories for this word
      const correctCategories = new Set(wordData.belongsIn);

      // Get the categories represented by the zone where the word is placed
      const zoneCategories = getZoneCategories(zoneId);

      // Compare the sets
      if (setsAreEqual(correctCategories, zoneCategories)) {
        correct.push(zoneId);
      } else {
        incorrect.push(zoneId);
      }
    });

    // Second pass: optimize for words that could be in multiple zones
    const wordsWithMultipleOptions = [];
    const optimizedCorrect = [...correct];
    const optimizedIncorrect = [...incorrect];

    // Find words that could potentially be in multiple zones
    Object.entries(placedWords).forEach(([zoneId, word]) => {
      const wordData = GAME_DATA.words.find((w) => w.word === word);
      if (!wordData) return;

      const correctCategories = new Set(wordData.belongsIn);
      const possibleZones = [];

      // Find all zones where this word could potentially be correct
      Object.keys(DROP_ZONES).forEach((potentialZoneId) => {
        const potentialZoneCategories = getZoneCategories(potentialZoneId);
        if (setsAreEqual(correctCategories, potentialZoneCategories)) {
          possibleZones.push(potentialZoneId);
        }
      });

      if (possibleZones.length > 1) {
        wordsWithMultipleOptions.push({
          word,
          currentZone: zoneId,
          possibleZones,
          wordData,
        });
      }
    });

    // If we have words with multiple options, try to optimize
    if (wordsWithMultipleOptions.length > 0) {
      const bestSolution = findOptimalPlacement(
        placedWords,
        wordsWithMultipleOptions
      );

      // Update correct/incorrect based on optimal solution
      optimizedCorrect.length = 0;
      optimizedIncorrect.length = 0;

      Object.entries(bestSolution).forEach(([zoneId, word]) => {
        const wordData = GAME_DATA.words.find((w) => w.word === word);
        if (!wordData) {
          optimizedIncorrect.push(zoneId);
          return;
        }

        const correctCategories = new Set(wordData.belongsIn);
        const zoneCategories = getZoneCategories(zoneId);

        if (setsAreEqual(correctCategories, zoneCategories)) {
          optimizedCorrect.push(zoneId);
        } else {
          optimizedIncorrect.push(zoneId);
        }
      });
    }

    return {
      correct: optimizedCorrect,
      incorrect: optimizedIncorrect,
    };
  };

  // Helper function to find the optimal placement that maximizes correct words
  const findOptimalPlacement = (currentPlacements, wordsWithOptions) => {
    let bestPlacement = { ...currentPlacements };
    let bestScore = 0;

    // Generate all possible combinations of placements for words with multiple options
    const generateCombinations = (
      words,
      index = 0,
      currentCombination = {}
    ) => {
      if (index >= words.length) {
        // Evaluate this combination
        const testPlacement = { ...currentPlacements, ...currentCombination };
        const score = evaluatePlacement(testPlacement);

        if (score > bestScore) {
          bestScore = score;
          bestPlacement = { ...testPlacement };
        }
        return;
      }

      const word = words[index];
      for (const zoneId of word.possibleZones) {
        // Check if this zone is available (not occupied by another word in this combination)
        if (
          !Object.values(currentCombination).includes(word.word) ||
          currentCombination[zoneId] === word.word
        ) {
          generateCombinations(words, index + 1, {
            ...currentCombination,
            [zoneId]: word.word,
          });
        }
      }
    };

    generateCombinations(wordsWithOptions);
    return bestPlacement;
  };

  // Helper function to evaluate a placement and return the number of correct words
  const evaluatePlacement = (placement) => {
    let correctCount = 0;

    Object.entries(placement).forEach(([zoneId, word]) => {
      const wordData = GAME_DATA.words.find((w) => w.word === word);
      if (!wordData) return;

      const correctCategories = new Set(wordData.belongsIn);
      const zoneCategories = getZoneCategories(zoneId);

      if (setsAreEqual(correctCategories, zoneCategories)) {
        correctCount++;
      }
    });

    return correctCount;
  };

  const handleCheck = () => {
    buttonClickSound.play();

    const { correct, incorrect } = checkPlacements();

    // Lock correct words
    const newLockedWords = { ...lockedWords };
    correct.forEach((zoneId) => {
      newLockedWords[zoneId] = true;
    });
    setLockedWords(newLockedWords);

    // Return incorrect words to bank with shake animation
    const newIncorrectWords = [];
    incorrect.forEach((zoneId) => {
      const word = placedWords[zoneId];
      newIncorrectWords.push(word);

      // Remove from placed words
      setPlacedWords((prev) => {
        const newPlaced = { ...prev };
        delete newPlaced[zoneId];
        return newPlaced;
      });
    });

    // Add incorrect words back to available words
    setAvailableWords((prev) => [...prev, ...newIncorrectWords]);
    setIncorrectWords(newIncorrectWords);

    // Clear incorrect words after shake animation completes
    setTimeout(() => setIncorrectWords([]), 400);

    // Decrease guesses
    const newGuessesLeft = guessesLeft - 1;
    setGuessesLeft(newGuessesLeft);

    // Check if all words are correct
    const allCorrect =
      Object.keys(placedWords).length === 7 && correct.length === 7;

    if (allCorrect) {
      setGameState("won");
      setResultMessage(
        `You solved it in ${6 - newGuessesLeft} guess${
          6 - newGuessesLeft === 1 ? "" : "es"
        }!`
      );
      setShowResult(true);
    } else if (newGuessesLeft === 0) {
      setGameState("lost");
      setResultMessage("Game Over — here's the solution");
      setShowResult(true);
    }

    // Hide result after 5 seconds
    setTimeout(() => setShowResult(false), 5000);
  };

  const VENN_COLORS = [
    "#5780BD", // teal
    "#F8D462", // yellow
    "#E7A0A4", // pink
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        width: "100%",
        px: { xs: 1, sm: 2 },
        pt: { xs: 0.5, sm: 2 },
        pb: { xs: 0.5, sm: 2 },
        mx: "auto",
        gap: { xs: 0.5, sm: 1.5 },
        backgroundColor: "#F5F0EB",
        borderRadius: 0,
        boxShadow: "none",
        overflow: "hidden",
        // Mobile optimization: ensure everything fits in viewport
        height: { xs: "100vh", sm: "auto" },
        maxHeight: { xs: "100vh", sm: "none" },
      }}
    >
      {/* Single HUD at the top */}
      <GameHUD
        currentDate={currentDate}
        guessesLeft={guessesLeft}
        streak={streak}
        isStreakActive={isStreakActive}
        onHelp={toggleHowToModal}
      />

      {/* Venn Diagram Area - Reduced height for mobile */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          minHeight: { xs: 200, sm: 320 },
          maxHeight: { xs: "50vh", sm: "none" },
          aspectRatio: { xs: "1", sm: "1" },
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: 2,
          p: 0,
          mb: { xs: 0.25, sm: 1 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {/* Circle 1 - Top Left (Purple) */}
        <Box
          sx={{
            position: "absolute",
            top: "3%",
            left: "0%",
            width: "64%",
            height: "64%",
            borderRadius: "50%",
            bgcolor: VENN_COLORS[0],
            opacity: 0.5,
            zIndex: 1,
            pointerEvents: "none",
            transition: "all 0.2s",
          }}
        />
        {/* Circle 2 - Top Right (Blue) */}
        <Box
          sx={{
            position: "absolute",
            top: "3%",
            left: "36%",
            width: "64%",
            height: "64%",
            borderRadius: "50%",
            bgcolor: VENN_COLORS[1],
            opacity: 0.5,
            zIndex: 1,
            pointerEvents: "none",
            transition: "all 0.2s",
          }}
        />
        {/* Circle 3 - Bottom Center (Yellow) */}
        <Box
          sx={{
            position: "absolute",
            top: "30%",
            left: "18%",
            width: "64%",
            height: "64%",
            borderRadius: "50%",
            bgcolor: VENN_COLORS[2],
            opacity: 0.5,
            zIndex: 1,
            pointerEvents: "none",
            transition: "all 0.2s",
          }}
        />
        {/* Drop Zones */}
        {Object.entries(DROP_ZONES).map(([zoneId, zoneConfig]) => (
          <DropZone
            key={zoneId}
            zoneId={zoneId}
            zoneConfig={zoneConfig}
            placedWord={placedWords[zoneId]}
            onDrop={handleDrop}
            onRemove={handleRemoveWord}
            isLocked={lockedWords[zoneId]}
            showSolution={gameState === "lost"}
            getZoneCategories={getZoneCategories}
            setsAreEqual={setsAreEqual}
          />
        ))}
      </Box>

      {/* Word Bank - Multi-row, slightly reduced spacing */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: 1,
          p: { xs: 0.5, sm: 1.5 },
          mb: { xs: 0.25, sm: 1 },
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          minHeight: { xs: 56, sm: 70 },
          maxHeight: { xs: 140 },
        }}
      >
        <WordBank
          words={availableWords}
          onDrop={handleRemoveWord}
          incorrectWords={incorrectWords}
          guessesLeft={guessesLeft}
          showPulse={showPulse}
        />
      </Box>

      {/* Check Button - Reduced spacing */}
      <Box
        sx={{
          mt: { xs: 0.5, sm: 1.5 },
          mb: { xs: 1, sm: 2 },
          width: "100%",
          maxWidth: "420px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleCheck}
          disabled={
            gameState !== "playing" ||
            guessesLeft === 0 ||
            Object.keys(placedWords).length < 7
          }
          sx={{
            bgcolor: "#5780BD",
            color: "white",
            width: "100%",
            px: 4,
            py: { xs: 1, sm: 1.5 },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.1rem" },
            boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
            letterSpacing: 1,
            transition: "transform 0.1s",
            "&:active": { transform: "scale(0.97)" },
            "&:disabled": {
              bgcolor: "rgba(0,0,0,0.12)",
              color: "rgba(0,0,0,0.38)",
            },
          }}
        >
          CHECK
        </Button>
        {showResult && (
          <Fade in={showResult}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ textAlign: "center" }}>
                {resultMessage}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Result Message */}
      {showResult && (
        <Fade in={showResult}>
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              p: 3,
              borderRadius: 2,
              boxShadow: 4,
              zIndex: 1000,
              maxWidth: "90vw",
              width: "400px",
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
              {resultMessage}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: "text.secondary", mb: 2 }}
            >
              The three themes were:
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {THEMES.map((theme) => (
                <Chip
                  key={theme}
                  label={theme}
                  sx={{
                    bgcolor: "rgba(143, 189, 196, 0.2)",
                    fontWeight: 600,
                    "& .MuiChip-label": {
                      color: "#1a1a1a",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Fade>
      )}

      {/* How to Play Modal */}
      <HowToPlayModal open={showHowToModal} onClose={toggleHowToModal} />
    </Box>
  );
};

export default CommonThreeBoard;
